import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import clsx from 'clsx';

// Types
import type { PostPreviewType } from '@/components/composite/Scheduler/Funnel/PostPreviews/types/PostPreviewType';
import type { SocialProfileSchedulerType } from '@/components/composite/Scheduler/Type/SocialProfileSchedulerType';

// Components
import Icon from '@/components/base/Icon';
import { MediaCarousel } from './components/MediaCarousel';

export type MediaOverlayProps<TNetwork extends SocialProfileSchedulerType['social_network_type']> =
    React.HTMLAttributes<HTMLDivElement> & {
        media: PostPreviewType<TNetwork>['postData']['data']['medias'];
        currentIndex: number;
    };

export function MediaOverlay<TNetwork extends SocialProfileSchedulerType['social_network_type']>({
    children,
    className,
    media,
    currentIndex,
    ...props
}: MediaOverlayProps<TNetwork>) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(currentIndex);

    const overlayRef = useRef<HTMLDivElement>(null);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const previousActiveIndexRef = useRef<number>(currentIndex);

    useEffect(() => {
        if (isOpen) {
            overlayRef.current?.focus();
        }
    }, [isOpen]);

    // Handle video visibility changes when active index changes
    useEffect(() => {
        // Stop previous video if it exists
        const previousVideo = videoRefs.current[previousActiveIndexRef.current];
        if (previousVideo && media[previousActiveIndexRef.current]?.type === 'video') {
            previousVideo.pause();
            previousVideo.currentTime = 0;
        }

        // Play new video if it exists
        const currentVideo = videoRefs.current[activeIndex];
        if (currentVideo && media[activeIndex]?.type === 'video') {
            currentVideo.play();
        }

        // Update previous active index
        previousActiveIndexRef.current = activeIndex;
    }, [activeIndex, media]);

    const handleOpen = () => setIsOpen(true);

    const handleClose = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setActiveIndex(currentIndex);
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (['Enter', 'Escape'].includes(e.key)) {
            handleClose();
        }

        if (e.key === 'ArrowLeft' && activeIndex > 0) {
            setActiveIndex((prev) => prev - 1);
        }

        if (e.key === 'ArrowRight' && activeIndex < media.length - 1) {
            setActiveIndex((prev) => prev + 1);
        }
    };

    const handleContentClick = (e: React.MouseEvent) => e.stopPropagation();

    const handlePrevious = () => {
        if (activeIndex > 0) {
            setActiveIndex((prev) => prev - 1);
        }
    };

    const handleNext = () => {
        if (activeIndex < media.length - 1) {
            setActiveIndex((prev) => prev + 1);
        }
    };

    return (
        <div
            {...props}
            className={clsx('relative', className)}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}>
            {children}

            {isOpen &&
                createPortal(
                    <div
                        ref={overlayRef}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
                        onClick={handleClose}
                        role="button"
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                        data-testid="media-overlay-background">
                        <div role="presentation" onClick={handleContentClick}>
                            <MediaCarousel
                                media={media}
                                activeIndex={activeIndex}
                                onPrevious={handlePrevious}
                                onNext={handleNext}
                            />
                        </div>

                        <button
                            className="absolute right-8 top-8 flex h-4 w-4 items-center justify-center rounded-full border border-white p-5 text-white"
                            onClick={handleClose}
                            data-testid="media-overlay-close-button">
                            <Icon icon="close" />
                        </button>
                    </div>,
                    document.body
                )}
        </div>
    );
}
